/* istanbul ignore file */
import useSWR from 'swr';
import { useIntl } from 'react-intl';

import { useSession } from '@packages/utilities';
import type {
  BasketPowerEfficiencyFlag,
  ProductPrice,
} from '@packages/gql/generated/shopping/graphql';
import { addBranchToOrderUrlPath } from '../../../../helpers';

export type ServiceItem = {
  label: string;
  services: Array<{
    description: string;
    iconUrl: string;
    isBooked: boolean;
    isIncluded: boolean;
    name: string;
    price: {
      currencyCode: string;
      value: number;
    };
    sku: string;
  }>;
};

// TODO(api-layer): delete unnecessary fetcher and use generated types after resolver are available in api-layer
export type BasketResponse = {
  targetPage: string;
  products: Array<{
    aplus?: Array<ServiceItem>;
    lineItemId: string;
    sku: string;
    description: string;
    extra: {
      breadcrumbs?: string[];
      promotion?: string;
      brand?: string;
      mkz?: string;
      size?: string;
    };
    articleNumber: string;
    deliveryMessage: string;
    deliveryTextColor: string;
    imageUrl: string;
    isSoldOut: boolean;
    name: string;
    price: ProductPrice;
    quantity: number;
    singlePrice: { currencyCode: string; value: number };
    sustained: boolean;
    variation: string;
    powerEfficiency?: Array<
      Pick<BasketPowerEfficiencyFlag, 'link' | 'icon' | 'dataSheet'> &
        Partial<Pick<BasketPowerEfficiencyFlag, 'text' | 'iconText'>>
    >;
  }>;
};

// TODO(api-layer): delete unnecessary fetcher and use generated types after resolver are available in api-layer
const fetcher =
  (locale: string) =>
  ([url, token]: [string, string]): Promise<BasketResponse> =>
    fetch(url, { headers: { 'x-ec-token': token, 'ec-locale': locale } }).then((res) => {
      if (!res.ok) {
        throw new Error(`Error fetching basket data: ${res.status} ${res.statusText}`);
      }
      return res.json();
    });

export const useBasketData = (lineItemId: string | null) => {
  const { jwtToken } = useSession();
  const { locale } = useIntl();
  const { data } = useSWR(
    jwtToken && lineItemId
      ? [addBranchToOrderUrlPath('/api/checkout/v1/'), jwtToken, lineItemId]
      : null,
    fetcher(locale),
    {
      suspense: true,
    },
  );
  return { data };
};
